import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { authFetch } from '@/api/client'
import { queryKeys } from '@/api/queryKeys'
import { ApiError, type ProblemDetail } from '@/lib/errors'
import type { components } from '@/../types/api'

/**
 * Hooks for the post-publish presentation editor — regenerate / apply
 * a structurally different title + thumbnail package on a video that's
 * already live on YouTube. The deterministic verdicts in the retention
 * audit feed the dialog this drives; nothing here writes to YouTube on
 * its own — apply is the only mutation that does, and the user has
 * always confirmed in the preview before that fires.
 */

export type PresentationProposal = components['schemas']['PresentationProposalOut']
export type PresentationApplyResult = components['schemas']['PresentationApplyOut']
export type PresentationHistoryResult = components['schemas']['PresentationHistoryOut']
export type PresentationHistoryEntry = components['schemas']['PresentationHistoryEntry']

interface Envelope<T> {
  data: T
}

function isProblemDetail(body: unknown): body is ProblemDetail {
  if (!body || typeof body !== 'object') return false
  const obj = body as Record<string, unknown>
  return typeof obj['title'] === 'string' && typeof obj['status'] === 'number'
}

async function readEnvelope<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null
    try {
      body = await res.clone().json()
    } catch {
      /* ignore */
    }
    const problem = isProblemDetail(body) ? body : null
    const message = problem?.detail ?? problem?.title ?? ''
    throw new ApiError(message, res.status, problem, body)
  }
  const json = (await res.json()) as Envelope<T>
  return json.data
}

// ---------------------------------------------------------------------------
// Regenerate — POST a list of rejected titles/styles, get a new proposal back.
// Not a useQuery because the dialog calls it imperatively (open + each "gerar
// outra opção" click). useMutation gives us isPending/error per call.
// ---------------------------------------------------------------------------

export interface RegenerateInput {
  videoId: string
  language: string
  rejectedTitles: string[]
  rejectedThumbStyles: string[]
  /** When false (dialog auto-fire), the server returns the saved draft if
   * one exists — no LLM call, no thumbnail re-render. When true (explicit
   * "Gerar outra opção" button), the server overwrites the saved draft
   * with a fresh proposal. */
  force?: boolean
}

// Worst case observed end-to-end is ~90s (LLM call + cold-start Chromium
// frame extraction + render). 180s gives 2x headroom; past that the fetch
// is almost certainly hung — proxy dropped the connection but the browser
// kept waiting on bytes that won't arrive — and we want the dialog to fall
// into a recoverable error state instead of "loader for life".
const REGEN_TIMEOUT_MS = 180_000

export function useRegeneratePresentation() {
  return useMutation({
    mutationFn: async (input: RegenerateInput): Promise<PresentationProposal> => {
      const params = new URLSearchParams({ lang: input.language })
      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => { controller.abort() }, REGEN_TIMEOUT_MS)
      try {
        const res = await authFetch(
          `/api/v1/videos/${encodeURIComponent(input.videoId)}/presentation/regenerate?${params.toString()}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              rejected_titles: input.rejectedTitles,
              rejected_thumb_styles: input.rejectedThumbStyles,
              force: input.force ?? false,
            }),
            signal: controller.signal,
          },
        )
        return await readEnvelope<PresentationProposal>(res)
      } catch (err) {
        // AbortError surfaces as DOMException with name 'AbortError' in the
        // browser. Re-throw with a friendlier message so the catch in
        // runRegenerate can surface "tempo esgotado" instead of the raw
        // network-error string.
        if (err instanceof DOMException && err.name === 'AbortError') {
          throw new Error('Tempo esgotado gerando a proposta (>180s). Tente de novo.')
        }
        throw err
      } finally {
        window.clearTimeout(timeoutId)
      }
    },
  })
}

// ---------------------------------------------------------------------------
// Apply — pushes a proposal to YouTube + writes one history row. Invalidates
// the retention audit + history queries so the operator sees the new state.
// ---------------------------------------------------------------------------

export interface ApplyInput {
  videoId: string
  language: string
  previewId: string
  newTitle: string
  newDescription: string
  newHashtags: string[]
  newThumbnailText: string
  newHighlightWord: string
  newThumbnailStyle: string
  reason: string
}

export function useApplyPresentation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: ApplyInput): Promise<PresentationApplyResult> => {
      const params = new URLSearchParams({ lang: input.language })
      const res = await authFetch(
        `/api/v1/videos/${encodeURIComponent(input.videoId)}/presentation/apply?${params.toString()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            preview_id: input.previewId,
            new_title: input.newTitle,
            new_description: input.newDescription,
            new_hashtags: input.newHashtags,
            new_thumbnail_text: input.newThumbnailText,
            new_highlight_word: input.newHighlightWord,
            new_thumbnail_style: input.newThumbnailStyle,
            reason: input.reason,
          }),
        },
      )
      return readEnvelope<PresentationApplyResult>(res)
    },
    onSuccess: (_data, input) => {
      // Verdicts depend on title + retention; freshen the audit table so the
      // edited row reflects the new title (and possibly a new verdict next
      // tick once snapshots settle).
      void queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.presentation.history(input.videoId, input.language),
      })
    },
  })
}

// ---------------------------------------------------------------------------
// History — paginated list of past edits for one video. Read-only.
// ---------------------------------------------------------------------------

export interface UseHistoryParams {
  videoId: string
  language: string
  limit?: number
  enabled?: boolean
}

export function usePresentationHistory({ videoId, language, limit = 50, enabled = true }: UseHistoryParams) {
  return useQuery({
    queryKey: queryKeys.presentation.history(videoId, language),
    queryFn: async (): Promise<PresentationHistoryEntry[]> => {
      const params = new URLSearchParams({ lang: language, limit: String(limit) })
      const res = await authFetch(
        `/api/v1/videos/${encodeURIComponent(videoId)}/presentation/history?${params.toString()}`,
      )
      const data = await readEnvelope<PresentationHistoryResult>(res)
      return data.items
    },
    enabled: enabled && Boolean(videoId && language),
    staleTime: 30_000,
  })
}
